import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { ClinicCard, DocumentCard } from "../../components/Cards/Cards";
import ProcessSuccess from "../../components/ProcessSuccess/ProcessSuccess";
import { ArrowIcon } from "../../front-end-global-components/assets/assets";
import Button from "../../front-end-global-components/components/Button/Button";
import FeatureFlagsFallback from "../../front-end-global-components/components/FeatureFlagsFallback/FeatureFlagsFallback";
import Header from "../../front-end-global-components/components/Header/Header";
import RoundedProfilePicture from "../../front-end-global-components/components/RoundedProfilePicture/RoundedProfilePicture";
import { searchAbdmClinic } from "../../redux/abdm/actions";
import {
  addDocumentFilter,
  removeDocumentFilter,
  retryDataTransfer
} from "../../redux/documents/actions";
import {
  getPatientsCareContexts,
  setSelectedLinkedFacility
} from "../../redux/patients/actions";
import { isValidArray, isValidObject } from "../../utils/validators";

const LinkedFacilities = (props) => {
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedLinkedFacility(null);
    getPatientsCareContexts();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Header
        supportIconOnClick={() => navigate("/query")}
        title="Linked Facilities"
        backButtonOnClick={() => {
          navigate(-1);
        }}
        incident={props.app.featureFlags?.incident?.patients}
        incidentOnClick={() => navigate("/incidents")}
      />

      {props.app.featureFlags?.healthData ? (
        <Routes>
          <Route
            index
            element={
              <FacilitiesList
                patients={props.patients}
                setSelectedLinkedFacility={setSelectedLinkedFacility}
              />
            }
          />
          <Route
            path=":facilityId"
            element={
              <FacilityDetail
                documents={props.documents}
                patients={props.patients}
                abdm={props.abdm}
              />
            }
          />
          <Route
            path=":facilityId/success"
            element={
              <FacilityRecordFetchSuccess
                patients={props.patients}
                abdm={props.abdm}
              />
            }
          />
        </Routes>
      ) : (
        <FeatureFlagsFallback height="remaining-body-height" />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  patients: state.patients,
  documents: state.documents,
  abdm: state.abdm,
  app: state.app
});

export default connect(mapStateToProps)(LinkedFacilities);

const FacilityDetail = (props) => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [selectedCareContexts, setSelectedCareContexts] = useState([]);

  useEffect(() => {
    const careContext = props.documents.filters?.careContext;
    setSelectedCareContexts(Array.isArray(careContext) ? careContext : []);
  }, [props.documents.filters?.careContext]);

  const careContexts =
    props.patients.careContexts?.[props.patients.currentProfile]?.[
      facilityId
    ] || [];

  return (
    <>
      <ClinicCard
        clinicDetails={props.abdm?.clinics?.search?.results?.[facilityId]}
      />
      <div
        style={{ height: "calc(100% - 204px)" }}
        className="padding-larger display-flex flex-justify-content-space-between flex-direction-column"
      >
        <>
          <div className="text-align-center font-size-small text-uppercase padding-bottom-larger">
            Linked records
          </div>
          <div
            style={{ height: "calc(100% - 36px)" }}
            className="overflow-scroll hide-scroll-bar"
          >
            {careContexts.length > 0 ? (
              careContexts.map((data) => {
                const refNo = String(
                  data?.careContexts?.[0]?.referenceNumber ?? ""
                );

                return (
                  <div key={refNo} data-cy={`document-${refNo}`}>
                    <DocumentCard
                      checkBoxName={refNo}
                      checkBoxOnChange={() => {
                        if (!refNo) return;
                        setSelectedCareContexts((prev) =>
                          prev?.includes(refNo)
                            ? prev.filter((d) => d !== refNo)
                            : [...prev, refNo]
                        );
                      }}
                      checkBoxChecked={
                        refNo ? selectedCareContexts?.includes(refNo) : false
                      }
                      data={{
                        display: data?.careContexts?.[0]?.display,
                        referenceNumber: refNo
                      }}
                    />
                  </div>
                );
              })
            ) : (
              <div>No Records Found</div>
            )}
          </div>
        </>
        <div className="display-flex">
          <Button
            text="Pull records"
            variant="primary"
            type="button"
            boxShadow={false}
            loading={props.documents.loading}
            disabled={selectedCareContexts?.length > 0 ? false : true}
            className="margin-top-default"
            data-cy="CONTINUE-BUTTON"
            onClick={() => {
              const consentDetail =
                props.patients.careContexts?.[props.patients.currentProfile]?.[
                  facilityId
                ]?.[0];

              if (isValidArray(selectedCareContexts)) {
                retryDataTransfer(
                  {
                    healthId: consentDetail?.referenceNumber,
                    hipId: consentDetail?.hip?.id,
                    careContexts: selectedCareContexts.map((id) => ({
                      careContextReference: id,
                      patientReference: consentDetail?.referenceNumber
                    }))
                  },
                  navigate
                );
              }
            }}
          />

          <Button
            text="View Details"
            variant="secondary"
            type="button"
            boxShadow={false}
            loading={props.documents.loading}
            className="margin-top-default margin-left-large"
            data-cy="CONTINUE-BUTTON"
            onClick={() => {
              removeDocumentFilter?.("doctorAndClinic");
              addDocumentFilter?.("clinic", facilityId);
              addDocumentFilter?.(
                "careContextDocumentId",
                selectedCareContexts
              );
              navigate("/patientTimeline");
            }}
          />
        </div>
      </div>
    </>
  );
};

const FacilityRecordFetchSuccess = (props) => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  return (
    <>
      <ClinicCard
        clinicDetails={props.abdm?.clinics?.search?.results?.[facilityId]}
      />
      <div
        style={{ height: "calc(100% - 210px)" }}
        className="padding-larger display-flex flex-justify-content-space-between flex-direction-column"
      >
        <ProcessSuccess
          title="Records fetched successfully"
          description={`YOUR RECORDS FROM ${props.abdm?.clinics?.search?.results?.[facilityId]?.identifier?.name}  WILL BE SHOW IN FEW MINUTES`}
          confirmClick={() => {
            navigate("/");
          }}
        />
      </div>
    </>
  );
};

const FacilitiesList = (props) => {
  const navigate = useNavigate();

  const careContext = isValidObject(
    props.patients.careContexts?.[props.patients.currentProfile]
  )
    ? Object.values(props.patients.careContexts[props.patients.currentProfile])
    : [];

  const clinics = [
    ...new Map(
      careContext
        ?.flat()
        .filter((data) => data.hip.type === "HIP" || data.hip.type === "HIU")
        .map((data) => ({
          id: data.hip.id,
          name: data.hip.name
        }))
        .map((item) => [item.id, item])
    ).values()
  ];

  const lockers = [
    ...new Map(
      careContext
        ?.flat()
        .filter((data) => data.hip.type === "HEALTH_LOCKER")
        .map((data) => ({
          id: data.hip.id,
          name: data.hip.name
        }))
        .map((item) => [item.id, item])
    ).values()
  ];

  if (!props.patients.currentProfile || careContext.length === 0) {
    return (
      <div className="font-family-gilroy-medium font-color-secondary display-flex flex-align-items-center flex-justify-content-center remaining-body-height">
        No linked facilities found
      </div>
    );
  }

  return (
    <div className="padding-horizontal-larger">
      {props.patients.loading && (
        <>
          <div className="font-size-small display-flex flex-justify-content-center padding-top-large text-uppercase">
            Hospitals / Clinics
          </div>
          {[...Array(8)].map((_data, index) => (
            <div key={index} className="padding-vertical-medium">
              <div className="display-flex flex-align-items-center padding-top-large">
                <div className="shimmer padding-larger border-radius-50-percentage height-width-16px" />
                <div className="margin-left-default">
                  <div className="shimmer padding-default border-radius-default width-200px" />
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {!props.patients.loading && clinics.length > 0 && (
        <>
          <div className="font-size-small display-flex flex-justify-content-center padding-top-large text-uppercase">
            Hospitals / Clinics
          </div>
          {clinics.map((d, idx) => (
            <FacilityRow
              key={idx}
              data={d}
              onClick={() => {
                searchAbdmClinic(d.name);
                setSelectedLinkedFacility(d.id);
                navigate(d.id);
              }}
            />
          ))}
        </>
      )}

      {!props.patients.loading && lockers.length > 0 && (
        <>
          <div className="font-size-small display-flex flex-justify-content-center padding-top-larger text-uppercase">
            Health Lockers
          </div>
          {lockers.map((d, idx) => (
            <FacilityRow
              key={idx}
              data={d}
              onClick={() => {
                searchAbdmClinic(d.hip.name);
                setSelectedLinkedFacility(d.hip.id);
                navigate(d.hip.id);
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};

const FacilityRow = (props) => (
  <div
    className="display-flex flex-align-items-center flex-justify-content-space-between padding-vertical-medium"
    data-cy={`facility-${props.data.id}`}
    onClick={props.onClick}
  >
    <div className="display-flex flex-align-items-center">
      <RoundedProfilePicture size="medium" src="" alt="" />
      <div className="padding-left-large font-color-secondary">
        <div className="font-size-default padding-top-small">
          {props.data.name}
        </div>
      </div>
    </div>
    <ArrowIcon />
  </div>
);
